import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Route, RouteComponentProps } from "react-router";

import {
  Button,
  Grid,
  TextField,
  makeStyles,
  Dialog,
  Card,
  CardContent,
} from "@material-ui/core";
import IF from "../../common/IF";

import { ApplicationState } from "../../../store";
import * as GalleryStore from "../../../store/Gallery";
import { Form, Formik } from "formik";
import { object, array, string } from "yup";
import ImageBrowser, { BrowserMode } from "../browse/ImageBrowser";
import { MultipleFileUploadField } from "../upload/MultipleFileUploadField";
import usePrepareLink from "../../../hooks/router/usePrepareLink";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiFormControl-root": {
      width: "60%",
      margin: theme.spacing(1),
    },
    "& .MuiButtonBase-root": {
      margin: theme.spacing(1, 0.5),
    },
  },
}));

export enum GalleryCreatePurpose {
  Album = 0,
  Image = 1,
}

interface GalleryCreateProps extends PropsFromRedux {
  purpose: GalleryCreatePurpose;
}

const GalleryCreate: React.FC<
  GalleryCreateProps & RouteComponentProps<{ albumId: string }>
> = ({ purpose, createGalleryElement, history, match }) => {
  const classes = useStyles();

  const [gallery, setGallery] = React.useState<GalleryStore.GalleryElement>({
    elementName: "",
    imageUrl: "",
    elementClassId: 3,
    id: 0,
    parentId: parseInt(match.params.albumId, 10) || 0,
    number: 0,
  });

  /// TODO: no need to release with first version
  const disableYoutubeSupport = true;

  const onSubmit = React.useCallback(
    async (event: React.ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (purpose === GalleryCreatePurpose.Album) {
        await createGalleryElement(gallery);
        history.goBack();
      } else if (purpose === GalleryCreatePurpose.Image) {
        await createGalleryElement(gallery);
        history.goBack();
      }
    },
    [purpose, gallery, createGalleryElement, history]
  );

  const popupBrowse = usePrepareLink({
    to:
      purpose === GalleryCreatePurpose.Album
        ? "/admin/albums/create/browse"
        : "/admin/albums/:albumId/images/create/browse",
    isRelativePath: false,
    hash: null,
  });

  const popupUpload = usePrepareLink({
    to:
      purpose === GalleryCreatePurpose.Album
        ? "/admin/albums/create/upload"
        : "/admin/albums/:albumId/images/create/upload",
    isRelativePath: false,
    hash: null,
  });

  return (
    <>
      <Route
        exact
        path={[
          "/admin/albums/create/upload",
          "/admin/albums/:albumId/images/create/upload",
        ]}
        children={({ match }) => (
          <Dialog
            maxWidth={false}
            onClose={history.goBack}
            open={Boolean(match)}
            scroll="body"
          >
            <Card style={{ width: 600 }}>
              <CardContent>
                <Formik
                  initialValues={{ files: [] }}
                  validationSchema={object({
                    files: array(
                      object({
                        url: string().required(),
                      })
                    ),
                  })}
                  onSubmit={async (values) => {
                    console.log("values", values);
                    await new Promise((res) => setTimeout(res, 500));
                  }}
                >
                  {({ values, errors, isValid, isSubmitting }) => (
                    <Form translate={undefined}>
                      <Grid container spacing={2} direction="column">
                        <MultipleFileUploadField name="files" />
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </CardContent>
            </Card>
          </Dialog>
        )}
      />
      <Route
        exact
        path={[
          "/admin/albums/create/browse",
          "/admin/albums/:albumId/images/create/browse",
        ]}
        children={({ match }) => (
          <Dialog
            maxWidth={false}
            onClose={history.goBack}
            open={Boolean(match)}
            scroll="body"
            disableBackdropClick={true}
          >
            <ImageBrowser
              browserMode={
                purpose === GalleryCreatePurpose.Album
                  ? BrowserMode.SingleMode
                  : BrowserMode.MultiMode
              }
              OnSelectFinished={(selectedItems) => {
                history.goBack();

                if(purpose !== GalleryCreatePurpose.Album && selectedItems.length > 1) {
                  setGallery({
                    ...gallery,
                    imageUrl: selectedItems.join(";"),
                  });
                } else {
                  setGallery({
                    ...gallery,
                    imageUrl: selectedItems[0],
                  });
                }
              }}
              OnClose={history.goBack}
            />
          </Dialog>
        )}
      />
      <form className={classes.root} onSubmit={onSubmit}>
        <Grid container direction="column">
          <TextField
            variant="outlined"
            required
            value={gallery.elementName}
            label="Display"
            size="small"
            onChange={(e) => {
              setGallery({
                ...gallery,
                elementName: e.target.value,
              });
            }}
          />
          <Grid container>
            <TextField
              disabled
              variant="outlined"
              required
              value={gallery.imageUrl}
              size="small"
              label="ImageUrl"
              onChange={(e) => {
                setGallery({
                  ...gallery,
                  imageUrl: e.target.value,
                });
              }}
            />
            <Button
              variant="contained"
              size="medium"
              color="default"
              onClick={() => {
                history.push(
                  popupBrowse.pathname.replace(":albumId", match.params.albumId)
                );
              }}
            >
              Browse
            </Button>
            <Button
              variant="contained"
              size="medium"
              color="default"
              onClick={() => {
                history.push(
                  popupUpload.pathname.replace(":albumId", match.params.albumId)
                );
              }}
            >
              Upload
            </Button>
            <IF
              condition={
                !disableYoutubeSupport && purpose === GalleryCreatePurpose.Image
              }
            >
              <TextField
                variant="outlined"
                label="Youtube Link"
                value={gallery.link}
                onChange={(e) => {
                  setGallery({
                    ...gallery,
                    link: e.target.value,
                  });
                }}
              />
              <TextField
                variant="outlined"
                value={gallery.link}
                label="Youtube Link"
              />
            </IF>
            <TextField
              variant="outlined"
              value={gallery.number}
              name="numberFormat"
              label="Number"
              size="small"
              onChange={(e) => {
                const target = Number(e.target.value);
                if (!isNaN(target) && target >= 0 && target < 10000) {
                  setGallery({
                    ...gallery,
                    number: target,
                  });
                }
              }}
              type="numberFormat"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </Grid>
          <Button
            variant="contained"
            size="large"
            type="submit"
            color="primary"
            style={{ width: 100 }}
          >
            Save
          </Button>
        </Grid>
      </form>
    </>
  );
};

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatch = {
  createGalleryElement: (element: GalleryStore.GalleryElement) =>
    GalleryStore.actionCreators.createGalleryElement(element),
};

const connector = connect(mapStateToProps, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(GalleryCreate);
